import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

// Caché en memoria (stale-while-revalidate) por clave = URL del GET.
// Permite que al volver a una página se muestre lo último cargado al instante
// mientras se revalida en segundo plano.
const store = new Map();

export function cacheGet(key) {
  return store.has(key) ? store.get(key) : undefined;
}

export function cacheSet(key, val) {
  store.set(key, val);
}

export function cachedFetch(key, setter) {
  return api.get(key).then((r) => {
    store.set(key, r.data);
    if (setter) setter(r.data);
    return r.data;
  });
}

export function invalidateCache(prefix) {
  if (!prefix) {
    store.clear();
    return;
  }
  for (const k of Array.from(store.keys())) {
    if (k.startsWith(prefix)) store.delete(k);
  }
}

// Hook para páginas simples: devuelve data (desde caché si existe), refetch y setData.
export function useCachedGet(key) {
  const [data, setData] = useState(() => (key ? cacheGet(key) ?? null : null));
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const refetch = () => {
    if (!key) return Promise.resolve();
    return cachedFetch(key, (d) => { if (mounted.current) setData(d); }).catch(() => {});
  };

  useEffect(() => {
    if (!key) return;
    const cached = cacheGet(key);
    if (cached !== undefined) setData(cached);
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const update = (val) => {
    setData((prev) => {
      const next = typeof val === "function" ? val(prev) : val;
      if (key) store.set(key, next);
      return next;
    });
  };

  return { data, setData: update, refetch };
}
